/**
 * FormBuilder-based implementation of the transaction editor.
 * Gated behind the `transactionDetailsV2` feature flag.
 *
 * Structure:
 *   TransactionEditFormProvider — provides FormProvider with initial input and a stable onSubmit
 *   TransactionEditFormProviderInner — bridges the view-model hook to the recursive transfer handoff/view
 *   useTransactionEditViewModel — owns form, Redux, workflow, and action orchestration
 *   TransactionEditDialogView — renders the prepared view model
 */

import React, { useCallback, useRef } from 'react';

import { transactionsTypes } from 'app-components/flux/transactions';
import type { TransactionDetailsInput } from 'app-components/flux/transactions/transactionDetailsForm';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';

import TransactionEditDialogView from './TransactionEditDialogView';
import { FormProvider } from '../hooks/formHooks';
import { useTransactionEditViewModel } from '../hooks/useTransactionEditViewModel';
import type { TransactionEditDialogProps } from '../types';

const defaultTxn = transactionsTypes.mkCashFlowTransaction({ state: 'CLEARED' });

type InnerProps = TransactionEditDialogProps & {
  saveFnRef: React.MutableRefObject<((txn: CashFlowTransaction) => void) | null>;
};

function TransactionEditFormProviderInner(props: InnerProps): React.JSX.Element {
  const { closeAll, otherTransfer, viewModel } = useTransactionEditViewModel(props);

  return otherTransfer ? (
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <TransactionEditFormProvider txn={otherTransfer} onClose={closeAll} rootModalDrawer={props.rootModalDrawer} />
  ) : (
    <TransactionEditDialogView viewModel={viewModel} rootModalDrawer={props.rootModalDrawer} />
  );
}

export default function TransactionEditFormProvider(props: TransactionEditDialogProps): React.JSX.Element {
  const { txn: txnProp, defaultAccountId } = props;

  // saveFnRef is filled in by the view-model hook each render so the outer
  // onSubmit always calls the most-recent saveTransactions closure.
  const saveFnRef = useRef<((txn: CashFlowTransaction) => void) | null>(null);

  const onSubmit = useCallback(({ payload }: { payload: CashFlowTransaction }) => {
    saveFnRef.current?.(payload);
  }, []);

  const formInputRef = useRef<TransactionDetailsInput | null>(null);
  if (!formInputRef.current) {
    formInputRef.current = { txn: txnProp || defaultTxn, defaultAccountId, txnWasProvided: !!txnProp };
  }
  const formInput = formInputRef.current;

  return (
    <FormProvider input={formInput} onSubmit={onSubmit}>
      <TransactionEditFormProviderInner {...props} saveFnRef={saveFnRef} />
    </FormProvider>
  );
}
